import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { ADMIN_NAV } from "@/lib/admin-nav";
import { supabase } from "@/integrations/supabase/client";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { sendAdvisorInvite } from "@/lib/invites.functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/admin/applications")({
  head: () => ({
    meta: [
      { title: "Strategist Applications — Command Center" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <ProtectedRoute requiredRole="admin">
      <PortalShell title="Strategist Applications" nav={ADMIN_NAV}>
        <ApplicationsTable />
      </PortalShell>
    </ProtectedRoute>
  ),
});

interface AppRow {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  state: string | null;
  status: string;
  step_completed: number;
  fit_score: number | null;
  created_at: string;
  has_license: boolean | null;
}

const STATUS_PILL: Record<string, string> = {
  new: "bg-bronze-400/20 text-bronze-600",
  invited: "bg-navy-800/15 text-navy-800",
  accepted: "bg-success/15 text-success",
  declined: "bg-charcoal-500/15 text-charcoal-500",
};

function ApplicationsTable() {
  const [rows, setRows] = useState<AppRow[]>([]);
  const [inviteMap, setInviteMap] = useState<Record<string, { accepted: boolean; revoked: boolean }>>({});
  const [pending, setPending] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<AppRow | null>(null);
  const invite = useServerFn(sendAdvisorInvite);

  async function load() {
    const [appsRes, invitesRes] = await Promise.all([
      supabase
        .from("advisor_applications")
        .select("id, full_name, email, phone, state, status, step_completed, fit_score, created_at, has_license")
        .order("created_at", { ascending: false }),
      supabase.from("invites").select("email, accepted_at, revoked_at"),
    ]);
    const map: Record<string, { accepted: boolean; revoked: boolean }> = {};
    for (const inv of (invitesRes.data ?? []) as Array<{
      email: string;
      accepted_at: string | null;
      revoked_at: string | null;
    }>) {
      const e = inv.email.toLowerCase();
      const prev = map[e] ?? { accepted: false, revoked: false };
      if (inv.accepted_at) prev.accepted = true;
      if (inv.revoked_at) prev.revoked = true;
      map[e] = prev;
    }
    setInviteMap(map);
    setRows((appsRes.data ?? []) as AppRow[]);
  }

  useEffect(() => {
    void load();
  }, []);

  async function approveAndInvite(row: AppRow) {
    setPending(row.id);
    try {
      await invite({ data: { applicationId: row.id } });
      toast.success(`Invite sent to ${row.email}`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send invite.");
    } finally {
      setPending(null);
      setConfirm(null);
    }
  }

  return (
    <div className="mx-auto max-w-7xl">
      <h2
        className="font-display font-semibold text-navy-800"
        style={{ fontSize: "32px", letterSpacing: "-0.01em" }}
      >
        Strategist <em className="font-normal italic text-bronze-600">Applications</em>
      </h2>
      <p className="mt-3 max-w-2xl font-sans text-[13px] leading-relaxed text-charcoal-500">
        Review every strategist application. Approve and invite to send the candidate a
        secure link to set their own password and unlock the Strategist Console.
      </p>

      <div className="mt-8 overflow-hidden rounded-[4px] border border-bronze-600/20 bg-white shadow-soft">
        <table className="w-full">
          <thead className="border-b border-bronze-600/20 bg-cream-50">
            <tr className="text-left font-sans text-[10px] uppercase tracking-[0.18em] text-charcoal-500">
              <th className="px-4 py-3">Applicant</th>
              <th className="px-4 py-3">State</th>
              <th className="px-4 py-3">License</th>
              <th className="px-4 py-3 text-right">Fit Score</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Submitted</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-bronze-600/15">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-12 text-center font-sans text-[13px] text-charcoal-500"
                >
                  No applications yet.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const inviteState = inviteMap[r.email.toLowerCase()];
              const accepted = inviteState?.accepted;
              const status = accepted ? "accepted" : r.status;
              return (
                <tr key={r.id} className="hover:bg-cream-50/60">
                  <td className="px-4 py-3">
                    <p className="font-sans text-[13px] text-charcoal-900">{r.full_name}</p>
                    <p className="font-sans text-[11px] text-charcoal-500">{r.email}</p>
                    {r.phone && (
                      <p className="font-sans text-[11px] text-charcoal-500">{r.phone}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">
                    {r.state ?? "—"}
                  </td>
                  <td className="px-4 py-3 font-sans text-[12px] text-charcoal-900">
                    {r.has_license == null ? "—" : r.has_license ? "Yes" : "No"}
                  </td>
                  <td className="px-4 py-3 text-right font-display text-[14px] text-navy-800">
                    {r.fit_score ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-sm px-2 py-1 font-sans text-[10px] uppercase tracking-[0.16em] ${STATUS_PILL[status] ?? STATUS_PILL.new}`}
                    >
                      {status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-sans text-[11px] text-charcoal-500">
                    {new Date(r.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {accepted ? (
                      <span className="font-sans text-[11px] text-charcoal-500">Joined</span>
                    ) : r.status === "invited" ? (
                      <SecondaryButton
                        onClick={() => setConfirm(r)}
                        disabled={pending === r.id}
                      >
                        Resend invite
                      </SecondaryButton>
                    ) : (
                      <PrimaryButton
                        onClick={() => setConfirm(r)}
                        disabled={pending === r.id}
                      >
                        Approve &amp; Invite
                      </PrimaryButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-cream-50">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-navy-800">
              Send secure invite?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-sans text-[13px] text-charcoal-500">
              {confirm?.full_name} ({confirm?.email}) will receive a one-time link to
              set their own password and enter the Strategist Console.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirm && approveAndInvite(confirm)}
              disabled={!!pending}
            >
              {pending ? "Sending…" : "Send invite"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}